import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import type { PowerPoint } from '@/api/inverterDetail';
import { fetchInverterDrillChart } from '@/api/inverterDetail';
import { useI18n } from '@/i18n';
import { localizeChartLabel } from '@/lib/chartLabel';

const TABS = [
  { key: 'hourly',  label: 'Saatlik', color: '#a855f7', unit: 'kWh' },
  { key: 'daily',   label: 'Günlük',  color: '#14b8a6', unit: 'MWh' },
  { key: 'monthly', label: 'Aylık',   color: '#3b82f6', unit: 'MWh' },
  { key: 'yearly',  label: 'Yıllık',  color: '#f59e0b', unit: 'MWh' },
] as const;

type TabKey = typeof TABS[number]['key'];

type Props = {
  inverterId: number;
  hourly: PowerPoint[];
  daily: PowerPoint[];
  monthly: PowerPoint[];
  yearly: PowerPoint[];
};

function HighchartsAreaChart({ options, height }: { options: Highcharts.Options; height: number }) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const chart = Highcharts.chart(containerRef.current, options);
    return () => chart.destroy();
  }, [options]);

  return <div ref={containerRef} style={{ height: `${height}px` }} className="w-full" />;
}

export default function InverterPowerChart({ inverterId, hourly, daily, monthly, yearly }: Props) {
  const { t } = useI18n();
  const [active, setActive] = useState<TabKey>('monthly');

  // Drill state
  const [drillYear,  setDrillYear]  = useState<number | null>(null);
  const [drillMonth, setDrillMonth] = useState<number | null>(null);
  const [drillDay,   setDrillDay]   = useState<number | null>(null);
  const [drillData,  setDrillData]  = useState<PowerPoint[] | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const activeRef = useRef(active);
  activeRef.current = active;

  const baseMap: Record<TabKey, PowerPoint[]> = { hourly, daily, monthly, yearly };

  const chartData = useMemo<PowerPoint[]>(() => {
    if (drillData !== null) return drillData;
    return baseMap[active];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, drillData, hourly, daily, monthly, yearly]);

  const localizedCategories = useMemo(
    () => chartData.map((item) => localizeChartLabel(item.date_name, t)),
    [chartData, t],
  );

  const tab = TABS.find(t => t.key === active)!;

  const doDrill = useCallback(async (
    year: number, month: number | null, day: number | null,
  ) => {
    setDrillLoading(true);
    try {
      const rows = await fetchInverterDrillChart(inverterId, year, month, day);
      setDrillData(rows);
    } finally {
      setDrillLoading(false);
    }
  }, [inverterId]);

  const doDrillRef = useRef(doDrill);
  doDrillRef.current = doDrill;

  function handleTabClick(key: TabKey) {
    setActive(key);
    setDrillYear(null);
    setDrillMonth(null);
    setDrillDay(null);
    setDrillData(null);
  }

  function handleBack() {
    if (active === 'hourly') {
      setActive('daily');
      setDrillDay(null);
      setDrillData(null);
      if (drillYear !== null && drillMonth !== null) void doDrill(drillYear, drillMonth, null);
    } else if (active === 'daily') {
      setActive('monthly');
      setDrillMonth(null);
      setDrillDay(null);
      setDrillData(null);
      if (drillYear !== null) void doDrill(drillYear, null, null);
    } else if (active === 'monthly') {
      setActive('yearly');
      setDrillYear(null);
      setDrillMonth(null);
      setDrillData(null);
    }
  }

  const drillLabel = useMemo(() => {
    if (active === 'hourly' && drillYear !== null && drillMonth !== null && drillDay !== null) {
      const dayItem = drillData?.find(d => d.date_day === drillDay)
        ?? daily.find(d => d.date_year === drillYear && d.date_month === drillMonth && d.date_day === drillDay);
      return localizeChartLabel(dayItem?.date_name ?? `${drillDay}`, t);
    }
    if (active === 'daily' && drillYear !== null && drillMonth !== null) {
      const mItem = drillData?.find(d => d.date_month === drillMonth)
        ?? monthly.find(d => d.date_year === drillYear && d.date_month === drillMonth);
      return `${localizeChartLabel(mItem?.date_name ?? String(drillMonth), t)} ${drillYear}`;
    }
    if (active === 'monthly' && drillYear !== null) {
      return `${drillYear}`;
    }
    return null;
  }, [active, drillYear, drillMonth, drillDay, drillData, daily, monthly]);

  const isDrilled = drillLabel !== null;
  const canDrillDown = active !== 'hourly';

  const highchartsOptions = useMemo<Highcharts.Options>(() => ({
    chart: {
      type: 'line',
      backgroundColor: 'transparent',
      spacingTop: 4,
      spacingLeft: 0,
      spacingRight: 0,
      spacingBottom: 4,
      style: { fontFamily: 'Inter, system-ui, sans-serif' },
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { enabled: false },
    xAxis: {
      categories: localizedCategories,
      lineColor: '#e2e8f0',
      tickColor: '#e2e8f0',
      labels: {
        style: { color: '#64748b', fontSize: '10px', fontWeight: '600' },
        rotation: chartData.length > 20 ? -45 : 0,
      },
    },
    yAxis: {
      title: { text: undefined },
      gridLineColor: '#f8fafc',
      gridLineDashStyle: 'Dash',
      labels: { style: { color: '#94a3b8', fontSize: '10px' } },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      borderRadius: 12,
      borderColor: '#cbd5e1',
      backgroundColor: 'rgba(255,255,255,0.96)',
      shadow: false,
      padding: 0,
      formatter() {
        const point = this.points?.[0];
        const value = typeof point?.y === 'number' ? point.y : 0;
        return `
          <div style="min-width:130px;padding:10px 12px">
            <div style="font-size:10px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;color:#64748b;margin-bottom:5px">
              ${this.x ?? ''}
            </div>
            <div style="display:flex;align-items:center;justify-content:space-between;gap:10px">
              <span style="font-size:11px;color:#0f172a">${t('inverterDetail.chart.tooltip')}</span>
              <span style="font-size:14px;font-weight:700;color:#0f172a">${value} ${tab.unit}</span>
            </div>
          </div>
        `;
      },
    },
    plotOptions: {
      line: {
        lineWidth: 2,
        marker: {
          enabled: chartData.length <= 15,
          radius: 4,
          symbol: 'circle',
          lineWidth: 2,
          lineColor: '#ffffff',
        },
        cursor: canDrillDown ? 'pointer' : 'default',
        point: {
          events: {
            click: function () {
              const currentTab = activeRef.current;
              if (currentTab === 'hourly') return;
              const idx = this.index;
              const item = chartData[idx];
              if (!item) return;

              if (currentTab === 'yearly' && item.date_year != null) {
                setDrillYear(item.date_year);
                setDrillMonth(null);
                setDrillDay(null);
                setDrillData(null);
                setActive('monthly');
                void doDrillRef.current(item.date_year, null, null);
              } else if (currentTab === 'monthly' && item.date_year != null && item.date_month != null) {
                setDrillYear(item.date_year);
                setDrillMonth(item.date_month);
                setDrillDay(null);
                setDrillData(null);
                setActive('daily');
                void doDrillRef.current(item.date_year, item.date_month, null);
              } else if (currentTab === 'daily' && item.date_year != null && item.date_month != null && item.date_day != null) {
                setDrillYear(item.date_year);
                setDrillMonth(item.date_month);
                setDrillDay(item.date_day);
                setDrillData(null);
                setActive('hourly');
                void doDrillRef.current(item.date_year, item.date_month, item.date_day);
              }
            },
          },
        },
      },
      series: {
        animation: { duration: 350 },
        states: { inactive: { opacity: 1 } },
      },
    },
    series: [
      {
        type: 'line',
        name: t('inverterDetail.chart.tooltip'),
        color: tab.color,
        data: chartData.map(d => parseFloat(String(d.power_total)) || 0),
      },
    ],
  }), [chartData, localizedCategories, tab, t, canDrillDown]);

  return (
    <div className="rounded-[28px] border border-slate-100 bg-white p-6 shadow-[0_20px_60px_-32px_rgba(15,23,42,0.35)] transition-all space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 min-w-0">
          {isDrilled && (
            <button
              onClick={handleBack}
              className="flex items-center gap-1 text-[9px] font-bold text-slate-400 hover:text-slate-700 transition-colors shrink-0"
            >
              ←
            </button>
          )}
          <h2 className="text-[12px] font-black text-slate-900 tracking-tight leading-none truncate">
            {t('inverterDetail.chart.title')}
            {drillLabel && (
              <span className="ml-1.5 text-[10px] font-semibold text-slate-400">{drillLabel}</span>
            )}
          </h2>
        </div>
        <div className="flex overflow-hidden rounded-xl border border-slate-100 bg-slate-50/50 shadow-sm">
          {TABS.map(tabDef => (
            <button
              key={tabDef.key}
              onClick={() => handleTabClick(tabDef.key)}
              className={`px-3 py-1 text-[9px] font-bold transition-all ${
                active === tabDef.key ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-500 hover:bg-white hover:text-slate-900'
              }`}
            >
              {tabDef.key === 'hourly'
                ? t('inverterDetail.chart.hourly')
                : tabDef.key === 'daily'
                  ? t('inverterDetail.chart.daily')
                  : tabDef.key === 'monthly'
                    ? t('inverterDetail.chart.monthly')
                    : t('inverterDetail.chart.yearly')}
            </button>
          ))}
        </div>
      </div>

      <div className="relative">
        <HighchartsAreaChart options={highchartsOptions} height={240} />
        {drillLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-xl">
            <div className="h-4 w-4 rounded-full border-2 border-slate-300 border-t-slate-700 animate-spin" />
          </div>
        )}
      </div>

      <p className="mt-1 text-sm text-slate-400">
        {active === 'hourly'  && t('inverterDetail.chart.rangeHourly',  { unit: tab.unit })}
        {active === 'daily'   && t('inverterDetail.chart.rangeDaily',   { unit: tab.unit })}
        {active === 'monthly' && t('inverterDetail.chart.rangeMonthly', { unit: tab.unit })}
        {active === 'yearly'  && t('inverterDetail.chart.rangeYearly',  { unit: tab.unit })}
      </p>
    </div>
  );
}
